import { ITask } from '@modules/TasksModule/types';
import { BodyText, Card, Divider } from '@servicetitan/design-system';
import { Flex } from '@servicetitan/anvil2';
import classNames from 'classnames';
import EllipseAdornment from '@pages/agent/InboxPage/components/TaskRow/EllipseAdornment/EllipseAdornment';
import { TaskText } from '@components/Task/TaskText/TaskText';
import styles from './SpeedToLeadTaskRow.module.css';
import { SpeedToLeadTaskAttributes } from '@modules/SpeedToLeadTasksModule/types';
import useCountdownTimer from '@lib/hooks/useCountDownTimer/useCountdownTimer';
import { STL_ACCEPTANCE_EXPIRY_DURATION_MS } from '@modules/SpeedToLeadTasksModule/acceptanceExpiry';

export interface SpeedToLeadTaskRowProps {
  task: ITask;
  active: boolean;
  onClick: () => void;
}

function formatCountdown(seconds: number): string {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export const SpeedToLeadTaskRow: React.FC<SpeedToLeadTaskRowProps> = ({
  task,
  active,
  onClick,
}) => {
  const attributes = task.attributes as SpeedToLeadTaskAttributes;
  const address = attributes.contact.address;
  const addressString = [
    address.street,
    address.unit,
    address.city,
    address.state,
    address.zip,
  ]
    .filter(Boolean)
    .join(', ');

  const isPreDial = !!attributes.expiryStartedAt && !attributes.hasMadeCall;
  const expiryAt =
    (attributes.expiryStartedAt ?? 0) + STL_ACCEPTANCE_EXPIRY_DURATION_MS;
  const { count } = useCountdownTimer(isPreDial ? expiryAt : 0);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <div data-testid={task.id}>
      <Card
        elevation='base'
        sharp
        onClick={handleClick}
        className={classNames(styles.taskCard, {
          [styles.taskCardActive]: active,
        })}
      >
        <Flex direction='column' gap='1'>
          <Flex justifyContent='space-between' alignItems='center'>
            <BodyText size='small' bold={active}>
              {task.displayName}
            </BodyText>
            {isPreDial && (
              <EllipseAdornment>
                <BodyText size='xsmall' subdued>
                  {formatCountdown(count)}
                </BodyText>
              </EllipseAdornment>
            )}
          </Flex>

          <BodyText size='xsmall' subdued>
            {addressString}
          </BodyText>

          {attributes.taskDetails.leadSource && (
            <TaskText
              label='Source:'
              text={attributes.taskDetails.leadSource}
            />
          )}
        </Flex>
      </Card>
      <Divider spacing='0' />
    </div>
  );
};
